<template lang="pug">
#fmaily-safety-confirm-detail
  .box
    .box-header
      .note
        span 利用者個別状況と施設全体状況を登録してメッセージ送信すると、ご家族が状況を確認できるようになります。
    .box-body
      b-tabs
        // resident status input tab
        b-tab.tab(title="①-1 利用者個別状況の登録")
          ResidentStatusTab(ref="residentStatusTab" :errorMessage="residentStatusErrorMessage"
            @onUpdate="confirmResidetStatus" @onCancel="gotoSafetyConfirmList")
        // facility status input tab
        b-tab.tab(title="①-2 施設全体状況の登録")
          FacilityStatusTab(ref="facilityStatusTab" :errorMessage="facilityStatusErrorMessage"
            @onUpdate="confirmFacilityStatus" @onCancel="gotoSafetyConfirmList")
        // send family safety confirm tab
        b-tab.tab(title="② メッセージ送信")
          SendFamilySafetyConfirmTab(ref="sendFamilySafetyConfirmTab" @onSendMessage="confirmSendMessage" @onCancel="gotoSafetyConfirmList")
        // family confirm result tab
        b-tab.tab(title="③ 家族確認結果・受付状態変更")
          FamilyConfirmResultTab(ref="familyConfirmResultTab" :errorMessage="familyConfirmResultErrorMessage"
            @onChangeStatus="confirmChangeStatus" @onBack="gotoSafetyConfirmList")

  // resident update modals
  OkCancelModal(v-model="showResidentStatusUpdateModal" title="安否情報提供（家族）の利用者個別状況を更新しますか？" @ok="updateResidentStatus")
  OkModal(v-model="showResidentStatusUpdateSuccessModal" title="更新に成功しました。" @ok="refreshTabs")
  // facility update modals
  OkCancelModal(v-model="showFacilityStatusUpdateModal" title="安否情報提供（家族）の施設全体状況を更新しますか？" @ok="updateFacilityStatus")
  OkModal(v-model="showFacilityStatusUpdateSuccessModal" title="更新に成功しました。" @ok="refreshTabs")
  // send message modals
  OkCancelModal(v-model="showSendMessageModal" title="安否情報提供（家族）メッセージを送信しますか？" @ok="sendMessage")
  OkModal(v-model="showSendMessageSuccessModal" title="メッセージ送信を受け付けました。" @ok="refreshTabs")
  // change status modals
  OkCancelModal(v-model="showChangeStatusModal" :title="changeStatusModalTitle" @ok="changeStatus")
  OkModal(v-model="showChangeStatusSuccessModal" title="受付状態の変更に成功しました。" @ok="refreshTabs")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import SafetyConfirmResidentStatusesService, {
  SafetyConfirmResidentStatusContents,
  SafetyConfirmResidentStatusPatchModel,
  SafetyConfirmResidentStatusPostModel
} from "@/commons/services/SafetyConfirmResidentStatuses";
import SafetyConfirmFacilityStatusesService, {
  SafetyConfirmFacilityStatusContents,
  SafetyConfirmFacilityStatusGetModel,
  SafetyConfirmFacilityStatusPatchModel,
  SafetyConfirmFacilityStatusPostModel
} from "@/commons/services/SafetyConfirmFacilityStatuses";
import SendSafetyConfirmMessagesService from "@/commons/services/SendSafetyConfirmMessages";
import SafetyConfirmsService, { FamilySafetyConfirmsPatchModel, SafetyConfirmsGetModel } from "@/commons/services/SafetyConfirms";

import ResidentStatusTab, { ResidentStatusTabOnUpdateModel } from "@/pc/containers/safetyConfirms/ResidentStatusTab.vue";
import FacilityStatusTab, { FacilityStatusTabOnUpdateModel } from "@/pc/containers/safetyConfirms/FacilityStatusTab.vue";
import SendFamilySafetyConfirmTab, { SendFamilySafetyConfirmTabOnSendMessageModel } from "@/pc/containers/safetyConfirms/SendFamilySafetyConfirmTab.vue";
import FamilyConfirmResultTab from "@/pc/containers/safetyConfirms/FamilyConfirmResultTab.vue";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import { ResidentStatusTableItem } from "@/pc/containers/safetyConfirms/ResidentStatusTable.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 家族への情報提供画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    ResidentStatusTab,
    FacilityStatusTab,
    SendFamilySafetyConfirmTab,
    FamilyConfirmResultTab
  }
})
export default class FamilySafetyConfirmDetail extends Vue {
  /** 安否確認(家族)利用者状況サービス */
  @Inject("SafetyConfirmResidentStatusesService") safetyConfirmResidentStatusesService!: SafetyConfirmResidentStatusesService;
  /** 安否確認(家族)施設状況サービス */
  @Inject("SafetyConfirmFacilityStatusesService") safetyConfirmFacilityStatusesService!: SafetyConfirmFacilityStatusesService;
  /** 安否確認(施設関係者)メッセージ送信サービス */
  @Inject("SendSafetyConfirmMessagesService") sendSafetyConfirmMessagesService!: SendSafetyConfirmMessagesService;
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;

  @Ref()
  residentStatusTab!: ResidentStatusTab;
  @Ref()
  facilityStatusTab!: FacilityStatusTab;
  @Ref()
  sendFamilySafetyConfirmTab!: SendFamilySafetyConfirmTab;
  @Ref()
  familyConfirmResultTab!: FamilyConfirmResultTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ページ全体ローディングメッセージ */
  loadingMessage: string = "読込み中...";

  /** 施設状況更新モーダル表示可否 */
  showResidentStatusUpdateModal: boolean = false;
  /** 更新対象の施設状況 */
  updateTargetResidentStatus: ResidentStatusTabOnUpdateModel | null = null;
  /** 施設状況更新完了モーダル表示可否 */
  showResidentStatusUpdateSuccessModal: boolean = false;
  /** 利用者状況エラーメッセージ */
  residentStatusErrorMessage: string = "";

  /** 施設状況更新モーダル表示可否 */
  showFacilityStatusUpdateModal: boolean = false;
  /** 更新対象の施設状況 */
  updateTargetFacilityStatus: FacilityStatusTabOnUpdateModel | null = null;
  /** 施設状況更新完了モーダル表示可否 */
  showFacilityStatusUpdateSuccessModal: boolean = false;
  /** 施設状況エラーメッセージ */
  facilityStatusErrorMessage: string = "";

  /** 安否確認(家族)送信モーダル表示可否 */
  showSendMessageModal = false;
  /** 送信対象のメッセージ情報 */
  sendMessageModel: SendFamilySafetyConfirmTabOnSendMessageModel | null = null;
  /** 安否確認(家族)送信完了モーダル表示可否 */
  showSendMessageSuccessModal: boolean = false;

  /** 安否確認(家族)の受付状態更新モーダル表示可否 */
  showChangeStatusModal: boolean = false;
  /** 安否確認(家族)の受付状態更新モーダルタイトル */
  changeStatusModalTitle: string = "";
  /** 更新対象の安否確認 */
  updateTargetSafetyConfirm: SafetyConfirmsGetModel | null = null;
  /** 安否確認(家族)の受付状態更新完了モーダル表示可否 */
  showChangeStatusSuccessModal: boolean = false;
  /** 施設状況エラーメッセージ */
  familyConfirmResultErrorMessage: string = "";

  /**
   * ### 利用者個別体状況の更新確認モーダルを表示する
   */
  public confirmResidetStatus(statuses: ResidentStatusTabOnUpdateModel): void {
    this.updateTargetResidentStatus = statuses;
    this.showResidentStatusUpdateModal = true;

    return;
  }

  /**
   * ### 利用者個別状況を更新する
   *
   * - 一時保存した利用者個別状況に登録前情報が存在するかで新規登録/更新を判定する
   */
  public async updateResidentStatus(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    const updateModel: ResidentStatusTabOnUpdateModel = this.updateTargetResidentStatus as ResidentStatusTabOnUpdateModel;

    const slicedStatusesList: ResidentStatusTableItem[][] = this.statusSlice(updateModel.updateStatuses, 50);
    for (const statuses of slicedStatusesList) {
      let promises: Array<Promise<void>> = [];

      for (const status of statuses) {
        // 新規登録
        if (status.updated == null) {
          const requestBody: SafetyConfirmResidentStatusPostModel = this.createSafetyConfirmResidentStatusPostRequestBody(status);
          promises.push(this.safetyConfirmResidentStatusesService.post(userStore, updateModel.safetyConfirmsId, status.residentId, 0, requestBody));
        }
        // 更新
        else {
          const requestBody: SafetyConfirmResidentStatusPatchModel = this.createSafetyConfirmResidentStatusPatchRequestBody(status);
          promises.push(this.safetyConfirmResidentStatusesService.patch(userStore, updateModel.safetyConfirmsId, status.residentId, 0, requestBody));
        }
      }

      await Promise.all(promises);
    }

    this.showFacilityStatusUpdateSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ## リクエストを並列実行するが、chromeの同時リクエスト数に上限があるので分割する
   */
  public statusSlice(status: Array<ResidentStatusTableItem>, sliceNum: number): Array<Array<ResidentStatusTableItem>> {
    let statusesList: Array<Array<ResidentStatusTableItem>> = [];

    for (let i: number = 0; i < Math.ceil(status.length / sliceNum); i++) {
      const start: number = i * sliceNum;
      const end: number = start + sliceNum;
      const slicedStatuses: Array<ResidentStatusTableItem> = status.slice(start, end);
      statusesList.push(slicedStatuses);
    }
    return statusesList;
  }

  /**
   * ### 施設全体状況の登録リクエストボディを作成する
   */
  public createSafetyConfirmResidentStatusPostRequestBody(status: ResidentStatusTableItem): SafetyConfirmResidentStatusPostModel {
    let statuses: Array<SafetyConfirmResidentStatusContents> = [];
    for (const item of Object.values(status.status)) {
      statuses.push({
        id: item.id,
        answer: item.answer
      });
    }

    const requestBody: SafetyConfirmResidentStatusPostModel = {
      statuses: statuses
    };
    return requestBody;
  }

  /**
   * ### 施設全体状況の更新リクエストボディを作成する
   */
  public createSafetyConfirmResidentStatusPatchRequestBody(status: ResidentStatusTableItem): SafetyConfirmResidentStatusPatchModel {
    let statuses: Array<SafetyConfirmResidentStatusContents> = [];
    for (const item of Object.values(status.status)) {
      statuses.push({
        id: item.id,
        answer: item.answer
      });
    }

    const requestBody: SafetyConfirmResidentStatusPatchModel = {
      statuses: statuses,
      updated: status.updated as number
    };

    return requestBody;
  }

  /**
   * ### 施設全体状況の更新確認モーダルを表示する
   */
  public confirmFacilityStatus(statuses: FacilityStatusTabOnUpdateModel): void {
    this.updateTargetFacilityStatus = statuses;
    this.showFacilityStatusUpdateModal = true;

    return;
  }

  /**
   * ### 施設全体状況を更新する
   *
   * - 一時保存した施設全体状況に登録前情報が存在するかで新規登録/更新を判定する
   */
  public async updateFacilityStatus(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const status: FacilityStatusTabOnUpdateModel = this.updateTargetFacilityStatus as FacilityStatusTabOnUpdateModel;

      // 新規登録
      if (status.beforeUpdateFacilityStatus == null) {
        const requestBody: SafetyConfirmFacilityStatusPostModel = this.createSafetyConfirmFacilityStatusPostRequestBody(status);
        await this.safetyConfirmFacilityStatusesService.post(userStore, status.safetyConfirmsId, requestBody);
      }
      // 更新
      else {
        const requestBody: SafetyConfirmFacilityStatusPatchModel = this.createSafetyConfirmFacilityStatusPatchRequestBody(status);
        await this.safetyConfirmFacilityStatusesService.patch(userStore, status.safetyConfirmsId, requestBody);
      }

      this.showFacilityStatusUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        const facilityStatusTab: FacilityStatusTab | Vue | Element | Vue[] | Element[] = this.$refs.facilityStatusTab;
        if (facilityStatusTab instanceof FacilityStatusTab) {
          facilityStatusTab.createView();
        }
        this.facilityStatusErrorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設全体状況の登録リクエストボディを作成する
   */
  public createSafetyConfirmFacilityStatusPostRequestBody(status: FacilityStatusTabOnUpdateModel): SafetyConfirmFacilityStatusPostModel {
    let statuses: Array<SafetyConfirmFacilityStatusContents> = [];
    for (const item of status.updateStatuses) {
      statuses.push({
        id: item.id,
        answer: item.answer
      });
    }

    const requestBody: SafetyConfirmFacilityStatusPostModel = {
      statuses: statuses
    };
    return requestBody;
  }

  /**
   * ### 施設全体状況の更新リクエストボディを作成する
   */
  public createSafetyConfirmFacilityStatusPatchRequestBody(status: FacilityStatusTabOnUpdateModel): SafetyConfirmFacilityStatusPatchModel {
    let statuses: Array<SafetyConfirmFacilityStatusContents> = [];
    for (const item of status.updateStatuses) {
      statuses.push({
        id: item.id,
        answer: item.answer
      });
    }

    const beforeUpdateFacilityStatus: SafetyConfirmFacilityStatusGetModel = status.beforeUpdateFacilityStatus as SafetyConfirmFacilityStatusGetModel;
    const requestBody: SafetyConfirmFacilityStatusPatchModel = {
      statuses: statuses,
      updated: beforeUpdateFacilityStatus.updated
    };

    return requestBody;
  }

  /**
   * ### 安否確認(家族)送信の更新確認モーダルを表示する
   */
  public confirmSendMessage(sendMessageModel: SendFamilySafetyConfirmTabOnSendMessageModel): void {
    this.sendMessageModel = sendMessageModel;
    this.showSendMessageModal = true;

    return;
  }

  /**
   * ### 安否確認(家族)を送信する
   */
  public async sendMessage(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "送信中...";

    const sendMessageModel: SendFamilySafetyConfirmTabOnSendMessageModel = this.sendMessageModel as SendFamilySafetyConfirmTabOnSendMessageModel;

    if (sendMessageModel.requestBodyForLine) {
      await this.sendSafetyConfirmMessagesService.sendFamilyMessage(userStore, sendMessageModel.safetyConfirmsId, sendMessageModel.requestBodyForLine);
    }
    if (sendMessageModel.requestBodyForMail) {
      await this.sendSafetyConfirmMessagesService.sendFamilyMails(userStore, sendMessageModel.safetyConfirmsId, sendMessageModel.requestBodyForMail);
    }

    this.isBusy = false;
    this.showSendMessageSuccessModal = true;

    return;
  }

  /**
   * ### 安否確認(家族)受付状態の変更確認モーダルを表示する
   */
  public confirmChangeStatus(safetyConfirm: SafetyConfirmsGetModel): void {
    this.updateTargetSafetyConfirm = safetyConfirm;

    if (safetyConfirm.acceptingFamilyReply == true) {
      this.changeStatusModalTitle = "受付状態を受付停止に変更しますか？";
    } else {
      this.changeStatusModalTitle = "受付状態を受付中に変更しますか？";
    }
    this.showChangeStatusModal = true;

    return;
  }

  /**
   * ### 安否確認(家族)受付状態を変更する
   */
  public async changeStatus(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const safetyConfirm: SafetyConfirmsGetModel = this.updateTargetSafetyConfirm as SafetyConfirmsGetModel;
      safetyConfirm.acceptingFamilyReply = !safetyConfirm.acceptingFamilyReply;

      const requestBody: FamilySafetyConfirmsPatchModel = {
        messagesSortKey: safetyConfirm.messagesSortKey ? (safetyConfirm.messagesSortKey as string[]) : null,
        acceptingFamilyReply: safetyConfirm.acceptingFamilyReply,
        updated: safetyConfirm.updated
      };
      await this.safetyConfirmsService.patchFamily(userStore, safetyConfirm.id, requestBody);

      this.showChangeStatusSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;

      if (systemError.statusCode == 409) {
        const familyConfirmResultTab: FamilyConfirmResultTab | Vue | Element | Vue[] | Element[] = this.$refs.familyConfirmResultTab;
        if (familyConfirmResultTab instanceof FamilyConfirmResultTab) {
          familyConfirmResultTab.createView();
        }
        this.familyConfirmResultErrorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 安否確認一覧画面に遷移する
   */
  public gotoSafetyConfirmList(): void {
    this.$router.push(PcUrl.SafetyConfirmList);

    return;
  }

  /**
   * ### 各タブの画面情報再作成
   *
   * - 再作成対象
   *   - 利用者個別状況タブ
   *   - 施設全体状況タブ
   *   - 送信タブ
   *   - 家族確認結果
   */
  public async refreshTabs(): Promise<void> {
    const promises: Array<Promise<void>> = [];

    if (this.residentStatusTab) {
      promises.push(this.residentStatusTab.refresh());
    }

    if (this.facilityStatusTab) {
      promises.push(this.facilityStatusTab.refresh());
    }

    if (this.sendFamilySafetyConfirmTab) {
      promises.push(this.sendFamilySafetyConfirmTab.refresh());
    }

    if (this.familyConfirmResultTab) {
      promises.push(this.familyConfirmResultTab.refresh());
    }

    await Promise.all(promises);

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      this.residentStatusTab.setFilterSetting();
    }
    next();
  }
}
</script>
